import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { FiArrowRight, FiCalendar, FiChevronDown, FiGift, FiMapPin } from "react-icons/fi";
import "./SEOPage.css";
import ScrollReveal from "./homePreview/ScrollReveal.jsx";

function FaqAccordion({ faqs }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => {
        const isOpen = index === openIndex;
        return (
          <div
            key={faq.q}
            className="overflow-hidden rounded-2xl border border-zoom-emerald/10 bg-white shadow-hp-xs"
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
              <span className="text-base font-semibold text-zoom-emeraldDeep sm:text-lg">{faq.q}</span>
              <FiChevronDown
                aria-hidden="true"
                className={`h-5 w-5 shrink-0 text-zoom-gold transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            <motion.div
              initial={false}
              animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <p className="px-5 pb-5 text-sm leading-7 text-zoom-charcoal/72 sm:text-base">{faq.a}</p>
            </motion.div>
          </div>
        );
      })}
    </div>
  );
}

function LinkChips({ items, variant = "solid" }) {
  const base =
    "min-h-[44px] inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold shadow-hp-xs transition hover:-translate-y-0.5";
  const styles =
    variant === "solid"
      ? `${base} bg-white border border-zoom-emerald/15 text-zoom-emeraldDeep hover:border-zoom-gold hover:bg-zoom-beige/60`
      : `${base} border border-zoom-gold/40 bg-zoom-beige/50 text-zoom-emeraldDeep hover:border-zoom-gold hover:bg-white`;

  return (
    <div className="flex flex-wrap gap-3">
      {items.map((item) => (
        <Link key={item.to} to={item.to} className={styles}>
          {item.label}
        </Link>
      ))}
    </div>
  );
}

function WhatsAppChips({ items }) {
  const hasImages = items.some((item) => item.image);

  if (!hasImages) {
    const styles =
      "min-h-[44px] inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold shadow-hp-xs transition hover:-translate-y-0.5 bg-white border border-zoom-emerald/15 text-zoom-emeraldDeep hover:border-zoom-gold hover:bg-zoom-beige/60";

    return (
      <div className="flex flex-wrap gap-3">
        {items.map((item) => (
          <a
            key={item.label}
            href={item.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className={styles}
          >
            {item.label}
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((item) =>
        item.image ? (
          item.to ? (
            <Link
              key={item.label}
              to={item.to}
              className="group flex flex-col transition duration-300 hover:-translate-y-1"
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-zoom-emerald/10 bg-white shadow-hp-md transition duration-300 group-hover:shadow-hp-glow">
                <img
                  src={item.image}
                  alt={item.label}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.08]"
                />
              </div>
              <span className="mt-3 flex items-center justify-center gap-1.5 text-center text-sm font-semibold leading-tight text-zoom-emeraldDeep">
                {item.label}
              </span>
              <span className="mt-1 flex items-center justify-center gap-1 text-center text-xs font-medium text-zoom-gold">
                View Options
              </span>
            </Link>
          ) : (
            <a
              key={item.label}
              href={item.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col transition duration-300 hover:-translate-y-1"
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-zoom-emerald/10 bg-white shadow-hp-md transition duration-300 group-hover:shadow-hp-glow">
                <img
                  src={item.image}
                  alt={item.label}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.08]"
                />
              </div>
              <span className="mt-3 flex items-center justify-center gap-1.5 text-center text-sm font-semibold leading-tight text-zoom-emeraldDeep">
                {item.label}
              </span>
              <span className="mt-1 flex items-center justify-center gap-1 text-center text-xs font-medium text-zoom-gold">
                <FaWhatsapp aria-hidden="true" /> Enquire
              </span>
            </a>
          )
        ) : item.to ? (
          <Link
            key={item.label}
            to={item.to}
            className="flex min-h-[44px] items-center justify-center rounded-3xl border border-zoom-emerald/15 bg-white px-4 py-2 text-center text-sm font-semibold text-zoom-emeraldDeep shadow-hp-xs transition hover:-translate-y-0.5 hover:border-zoom-gold hover:bg-zoom-beige/60"
          >
            {item.label}
          </Link>
        ) : (
          <a
            key={item.label}
            href={item.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-[44px] items-center justify-center rounded-3xl border border-zoom-emerald/15 bg-white px-4 py-2 text-center text-sm font-semibold text-zoom-emeraldDeep shadow-hp-xs transition hover:-translate-y-0.5 hover:border-zoom-gold hover:bg-zoom-beige/60"
          >
            {item.label}
          </a>
        )
      )}
    </div>
  );
}

export default function SEOPage({
  title,
  metaDescription,
  canonicalPath,
  h1,
  ogDescription,
  serviceName,
  serviceType,
  areaServed,
  intro,
  whatWeOffer,
  suitableOccasions,
  areaCoverage,
  faqs,
  ctaTitle,
  ctaText,
  whatsappLink,
  inquiryLabel,
  existingLinks = [],
  relatedSeoLinks = [],
  decorCategoryChips = [],
}) {
  const canonicalUrl = `https://www.decorzoom.in${canonicalPath}`;

  const infoCards = [
    { icon: FiGift, title: "What We Offer", body: whatWeOffer },
    { icon: FiCalendar, title: "Suitable Occasions", body: suitableOccasions },
    { icon: FiMapPin, title: "Area Coverage", body: areaCoverage },
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={ogDescription || metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: serviceName || h1,
            provider: {
              "@type": "LocalBusiness",
              name: "DecorZoom",
              url: "https://www.decorzoom.in",
              telephone: "+91-8377982191",
              image: "https://www.decorzoom.in/images/logo.png",
              areaServed: ["Delhi", "Noida", "Ghaziabad", "Gurgaon", "Faridabad"].map((cityName) => ({
                "@type": "City",
                name: cityName,
              })),
            },
            areaServed,
            serviceType,
            url: canonicalUrl,
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.decorzoom.in/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: h1,
                item: canonicalUrl,
              },
            ],
          })}
        </script>
        {faqs.length ? (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            })}
          </script>
        ) : null}
      </Helmet>

      <div className="font-body bg-zoom-warm text-zoom-charcoal">
        {/* Hero */}
        <section className="relative isolate overflow-hidden bg-[linear-gradient(135deg,#FDFBF7_0%,#F5F0E8_58%,#eee2c4_100%)]">
          <div className="mx-auto max-w-5xl px-4 pb-12 pt-10 sm:px-6 sm:pb-16 sm:pt-14">
            <ScrollReveal>
              {serviceType ? (
                <p className="inline-flex rounded-full border border-zoom-gold/45 bg-white/85 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-zoom-emeraldDeep shadow-hp-xs backdrop-blur">
                  {serviceType}
                </p>
              ) : null}

              <h1 className="font-display mt-5 text-3xl font-bold leading-[1.1] tracking-tight text-zoom-emeraldDeep sm:text-4xl lg:text-5xl">
                {h1}
              </h1>

              {intro.map((paragraph, index) => (
                <p
                  key={index}
                  className="mt-4 max-w-3xl text-base font-normal leading-8 text-zoom-charcoal/72 sm:text-lg"
                >
                  {paragraph}
                </p>
              ))}

              {areaServed && areaServed.length > 0 ? (
                <div className="mt-6 flex flex-wrap gap-2">
                  {areaServed.map((area) => (
                    <span
                      key={area}
                      className="rounded-full bg-zoom-emerald/10 px-3 py-1.5 text-xs font-semibold text-zoom-emeraldDeep"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              ) : null}
            </ScrollReveal>
          </div>
        </section>

        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          {/* Bento info grid */}
          <ScrollReveal delay={0.1} className="grid gap-5 sm:grid-cols-3">
            {infoCards.map(({ icon: Icon, title: cardTitle, body }) => (
              <div
                key={cardTitle}
                className="flex flex-col rounded-3xl border border-zoom-emerald/10 bg-white p-6 shadow-hp-md transition duration-300 hover:-translate-y-1 hover:shadow-hp-glow"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-zoom-emeraldDeep text-zoom-goldSoft">
                  <Icon aria-hidden="true" />
                </span>
                <h2 className="font-display mt-5 text-xl font-semibold leading-tight text-zoom-emeraldDeep">
                  {cardTitle}
                </h2>
                <p className="mt-3 text-sm font-normal leading-7 text-zoom-charcoal/68 sm:text-base">{body}</p>
              </div>
            ))}
          </ScrollReveal>

          {/* Explore related decor */}
          {existingLinks.length > 0 && (
            <ScrollReveal delay={0.12} className="mt-12">
              <h2 className="font-display text-2xl font-bold text-zoom-emeraldDeep">Explore Related Decor</h2>
              <div className="mt-5">
                <LinkChips items={existingLinks} variant="solid" />
              </div>
            </ScrollReveal>
          )}

          {/* Decor category chips (WhatsApp links) */}
          {decorCategoryChips.length > 0 && (
            <ScrollReveal delay={0.13} className="mt-12">
              <h2 className="font-display text-2xl font-bold text-zoom-emeraldDeep">Explore Decor Categories</h2>
              <div className="mt-5">
                <WhatsAppChips items={decorCategoryChips} />
              </div>
            </ScrollReveal>
          )}

          {/* FAQ */}
          {faqs.length > 0 && (
            <ScrollReveal delay={0.14} className="mt-12">
              <h2 className="font-display text-2xl font-bold text-zoom-emeraldDeep">Frequently Asked Questions</h2>
              <div className="mt-5">
                <FaqAccordion faqs={faqs} />
              </div>
            </ScrollReveal>
          )}

          {/* More local decoration pages */}
          {relatedSeoLinks.length > 0 && (
            <ScrollReveal delay={0.16} className="mt-12">
              <h2 className="font-display text-2xl font-bold text-zoom-emeraldDeep">More Local Decoration Pages</h2>
              <div className="mt-5">
                <LinkChips items={relatedSeoLinks} variant="outline" />
              </div>
            </ScrollReveal>
          )}

          {/* CTA */}
          <ScrollReveal
            delay={0.18}
            className="mt-14 overflow-hidden rounded-[32px] border border-white/15 bg-[linear-gradient(135deg,#0A3D3A_0%,#0B2822_72%,#172e14_100%)] p-6 text-white shadow-hp-dark sm:p-8 lg:p-10"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-zoom-goldSoft">Book Now</p>
            <h2 className="font-display mt-4 text-2xl font-bold tracking-tight sm:text-3xl">{ctaTitle}</h2>
            <p className="mt-4 max-w-2xl text-base font-normal leading-8 text-white/84">{ctaText}</p>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white shadow-hp-sm transition hover:-translate-y-0.5 hover:brightness-95"
            >
              <FaWhatsapp aria-hidden="true" /> {inquiryLabel} <FiArrowRight aria-hidden="true" />
            </a>
          </ScrollReveal>
        </div>
      </div>
    </>
  );
}
